/**
 * `sms_message.kind`. `customer_order_confirmation` is the second SMS of a phone order paid online: sent
 * once, by the worker, when the order's payment is confirmed paid (amended 2026-10-02; reserved until then).
 *
 * AMENDED 2026-10-29 (plan-029 SMS-GEN, design §40.10, ADR 0082 proposed, glossary §6.44 "confirmation code"):
 * `confirmation_code` is the one-time code a party is texted to confirm who is calling. It always belongs to a
 * conversation and never to an order; its row stores the body with the code replaced by `[code]` (the clear body goes
 * to the provider from memory, once); it is never resent. The migration that restates 0038's CHECK with it is plan-029
 * PC-MIG's; this list is 0038's verbatim plus that one value (the 0038 agreement test reads 0038's list as this
 * constant less the new value).
 */
export const SMS_MESSAGE_KIND = ["payment_link", "customer_order_confirmation", "confirmation_code"];
/** The kinds that belong to a customer order (plan-005): they carry the order's id and count in each other's caps. */
export const SMS_CUSTOMER_ORDER_MESSAGE_KIND = ["payment_link", "customer_order_confirmation"];
/** The kinds that belong to a conversation and never to an order (plan-029). */
export const SMS_CONVERSATION_MESSAGE_KIND = ["confirmation_code"];
/** True for a kind of {@link SMS_CUSTOMER_ORDER_MESSAGE_KIND}. */
export function isSmsCustomerOrderMessageKind(kind) {
    return SMS_CUSTOMER_ORDER_MESSAGE_KIND.includes(kind);
}
/**
 * The kinds one kind's caps count (design §40.10.1, ED-12, "caps filtered by kind"): an order kind counts the order
 * kinds only, exactly what every cap counted before plan-029 (no other kind existed), and `confirmation_code` counts
 * itself only, against its own figures. So a party texted three codes still receives the payment link they ask for, and
 * a busy shop's payment links never use up a caller's codes.
 */
export function smsCapKindOf(kind) {
    return isSmsCustomerOrderMessageKind(kind) ? SMS_CUSTOMER_ORDER_MESSAGE_KIND : SMS_CONVERSATION_MESSAGE_KIND;
}
/**
 * `sms_message.state`. `sending` (DD5-11) is the ambiguous state between the record and the provider's
 * answer: the backstop must never resend a message in it.
 */
export const SMS_MESSAGE_STATE = [
    "created",
    "sending",
    "accepted",
    "sent",
    "delivered",
    "failed",
    "rejected",
    "expired",
    "unknown",
];
/** States after which nothing more happens to a message. */
export const SMS_FINAL_STATE = ["delivered", "failed", "rejected", "expired"];
/** GSM 03.38 7-bit or UCS-2: decides how many characters fit a segment. */
export const SMS_ENCODING = ["gsm7", "ucs2"];
/**
 * Which SMS adapter an environment binds (DD5-10). `fake` is refused in production; `none` means SMS
 * is unavailable, so the voice agent never says "sent" for a message nobody sent.
 */
export const SMS_ADAPTER_BINDING = ["apifon", "fake", "none"];
/** `sms_message.provider_name`: the adapter that took the send. `none` never writes a row. */
export const SMS_PROVIDER_NAME = ["apifon", "fake"];
/** What triggered a send (`sms_message.trigger_kind`). */
export const SMS_TRIGGER_KIND = ["conversation", "person", "operator", "system"];
/** Who wrote a state change (`sms_message_transition.source`). */
export const SMS_TRANSITION_SOURCE = ["dispatch", "delivery_report", "worker_backstop", "final_bound"];
/**
 * The `sms_message.error_code` values a confirmation code's row is closed `failed` with when nothing was sent (design
 * §40.10.1, §40.10.2; plan-029 SMS-GEN and WRK-2). A code is never resent, so a `created` code row that was not sent
 * is closed rather than left for a backstop: `deadline_before_send` by the dispatch when the caller's wait ran out
 * before the send, `code_not_resent` by the worker's backstop when the runtime died between the record and the send.
 */
export const SMS_CONFIRMATION_CODE_UNSENT = Object.freeze({
    deadlineBeforeSend: "deadline_before_send",
    codeNotResent: "code_not_resent",
});
/** The idempotency key of an order-kind message (design §18.3.1). */
export function smsIdempotencyKeyOf(customerOrderId, kind, attemptOrdinal) {
    if (!Number.isSafeInteger(attemptOrdinal) || attemptOrdinal < 1) {
        throw new RangeError(`attemptOrdinal must be a positive integer; received ${String(attemptOrdinal)}`);
    }
    return `sms:${customerOrderId}:${kind}:${String(attemptOrdinal)}`;
}
/**
 * The idempotency key of a confirmation code (design §40.10.1, ED-12): `sms:conv:<conversation_id>:confirmation_code:
 * <ordinal>`, the code's ordinal within its conversation (1, 2, 3 …). The `conv:` segment keeps it apart from every
 * order key (`sms:<customer_order_id>:…`) whatever ids collide, so a duplicate ordinal is recognised and never sent twice.
 */
export function smsConfirmationCodeIdempotencyKeyOf(conversationId, ordinal) {
    if (!Number.isSafeInteger(ordinal) || ordinal < 1) {
        throw new RangeError(`ordinal must be a positive integer; received ${String(ordinal)}`);
    }
    return `sms:conv:${conversationId}:confirmation_code:${String(ordinal)}`;
}
//# sourceMappingURL=sms.js.map